import React from 'react';
import {
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
} from 'recharts';
import get from 'lodash.get';
import styled from 'styled-components/macro';
import { useTranslate } from '@federico.moretti/react-translate';
import { Text, NumberValue } from '../../typography';
import { FlexWrapper, hexToRgba, SPACINGS } from '../../utils';

const PieChartPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  border: ${SPACINGS[5]} solid ${(p) => p.theme.colors.secondary90w};
  border-radius: 50%;
`;

type PieChartProps = {
  data: any[];
  dataKey: string;
  dataColors: string[];
  labelKey?: string;
  currency?: string;
};

function PieChart(props: PieChartProps) {
  const { data, dataKey, dataColors, labelKey, currency = '' } = props;
  const { t } = useTranslate();

  const [valueToShowIndex, setValueToShowIndex] = React.useState<
    number | undefined
  >(undefined);

  const isEverythingZero = data.every((d) => get(d, dataKey) === 0);
  const dataWithoutZeroValues = data
    ? data
        .map((d, i) => ({ ...d, __originalIndex: i }))
        .filter((d) => get(d, dataKey) > 0)
    : [];

  if (!data || data.length === 0 || isEverythingZero) {
    return <PieChartPlaceholder />;
  }

  const sum: number = data.reduce((s, d) => s + get(d, dataKey), 0);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      {labelKey && (
        <FlexWrapper
          direction="column"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 2,
          }}
        >
          {valueToShowIndex === undefined ? (
            <>
              <Text align="center" type="label" htmlFor="center-label">
                {t('total')}
              </Text>
              <NumberValue
                id="center-label"
                type="currency"
                currency={currency}
                value={sum}
              />
            </>
          ) : (
            <>
              <Text align="center" type="label">
                {get(dataWithoutZeroValues[valueToShowIndex], labelKey)}
              </Text>
              <NumberValue
                type="currency"
                currency={currency}
                value={get(dataWithoutZeroValues[valueToShowIndex], dataKey)}
              />
            </>
          )}
        </FlexWrapper>
      )}
      <ResponsiveContainer>
        <RechartsPieChart>
          <Pie
            data={dataWithoutZeroValues}
            innerRadius="75%"
            outerRadius="100%"
            paddingAngle={2}
            dataKey={dataKey}
            startAngle={450}
            endAngle={90}
          >
            {dataWithoutZeroValues.map(({ __originalIndex }, i) => (
              <Cell
                key={`cell-${i}`}
                fill={hexToRgba(
                  dataColors[__originalIndex],
                  valueToShowIndex === i || valueToShowIndex === undefined
                    ? 1
                    : 0.5
                )}
                onMouseEnter={() => setValueToShowIndex(i)}
                onMouseLeave={() => setValueToShowIndex(undefined)}
              />
            ))}
          </Pie>
        </RechartsPieChart>
      </ResponsiveContainer>
    </div>
  );
}

export default PieChart;
